import React, { useState } from "react";
import { ethers } from "ethers";

const buttonStyle = {
    backgroundColor: "white",
    color: "#002147",
    padding: "10px 20px",
    border: "none",
    borderRadius: "5px",
    fontSize: "16px",
    fontWeight: "bold",
    cursor: "pointer",
    boxShadow: "0 2px 4px rgba(0, 0, 0, 0.2)",
    transition: "background-color 0.3s ease, opacity 0.3s ease",
    opacity: 1,
};

const LAMBDA_URL = "https://kim737zr7y4t6evmlongtnt2q40uiomg.lambda-url.eu-west-2.on.aws/";
const BASE_CHAIN_ID = 8453;
const BASE_PARAMS = {
    chainId: "0x2105",
    chainName: "Base",
    nativeCurrency: { name: "ETH", symbol: "ETH", decimals: 18 },
    rpcUrls: ["https://mainnet.base.org"],
    blockExplorerUrls: ["https://basescan.org"],
};
const USDC_ADDRESS = "0x833589fCD6eDb6E08f4c7C32D4f71b54bdA02913";
const USDC_ABI = [
    "function transfer(address to, uint256 amount) external returns (bool)",
    "function balanceOf(address account) external view returns (uint256)",
    "function decimals() external view returns (uint8)",
];

const DonateButton = () => {
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [paymentInstructions, setPaymentInstructions] = useState(null);

    const ensureWallet = () => {
        if (!window.ethereum) {
            throw new Error("Please install MetaMask or another Web3 wallet.");
        }
        return new ethers.BrowserProvider(window.ethereum);
    };

    const ensureBase = async (provider) => {
        const network = await provider.getNetwork();
        if (Number(network.chainId) === BASE_CHAIN_ID) return;

        const shouldSwitch = window.confirm(
            "Please switch to Base network to make this donation. Click OK to switch automatically."
        );
        if (!shouldSwitch) throw new Error("Donation requires Base network.");

        try {
            await window.ethereum.request({
                method: "wallet_switchEthereumChain",
                params: [{ chainId: BASE_PARAMS.chainId }],
            });
        } catch (switchError) {
            await window.ethereum.request({
                method: "wallet_addEthereumChain",
                params: [BASE_PARAMS],
            });
        }
    };

    const handlePayment = async (instructions) => {
        const provider = ensureWallet();
        await ensureBase(provider);

        await new Promise(resolve => setTimeout(resolve, 1000));

        const freshProvider = new ethers.BrowserProvider(window.ethereum);
        const signer = await freshProvider.getSigner();
        const userAddress = await signer.getAddress();

        const usdcContract = new ethers.Contract(USDC_ADDRESS, USDC_ABI, signer);

        // $5 USDC = 5000000 (6 decimals)
        const usdcAmount = ethers.parseUnits("5", 6);

        // Check USDC balance
        const usdcBalance = await usdcContract.balanceOf(userAddress);
        if (usdcBalance < usdcAmount) {
            throw new Error("Insufficient USDC balance. Please add USDC to your wallet.");
        }

        try {
            // Send USDC transfer
            const tx = await usdcContract.transfer(instructions.recipient, usdcAmount);
            const receipt = await tx.wait();
            return receipt.hash;
        } catch (txError) {
            console.error("Transaction error:", txError);

            if (txError.code === "CALL_EXCEPTION") {
                throw new Error("Transaction failed. Please ensure you have enough USDC and try again.");
            }
            if (txError.code === "ACTION_REJECTED") {
                throw new Error("Transaction was rejected. Please try again.");
            }
            throw new Error(txError.message || "Payment transaction failed. Please try again.");
        }
    };

    const handleDonate = async () => {
        setIsLoading(true);
        setError(null);
        setPaymentInstructions(null);

        try {
            let response;
            try {
                response = await fetch(LAMBDA_URL, {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                    },
                });
            } catch (fetchError) {
                console.error("Fetch error:", fetchError);
                throw new Error(
                    `Network error: ${fetchError.message}.`
                );
            }

            // Check if response is ok (status 200-299) or 402 (Payment Required)
            if (!response.ok && response.status !== 402) {
                const errorText = await response.text().catch(() => "Unknown error");
                console.error("Lambda error response:", response.status, errorText);
                throw new Error(
                    `Server error (${response.status}): ${errorText}`
                );
            }

            // Handle 402 Payment Required (x402 protocol)
            if (response.status === 402) {
                const paymentData = await response.json();
                const instructions = paymentData.paymentInstructions;

                setPaymentInstructions(instructions);

                try {
                    const paymentProof = await handlePayment(instructions);

                    const retryResponse = await fetch(LAMBDA_URL, {
                        method: "POST",
                        headers: {
                            "Content-Type": "application/json",
                            "x-payment": paymentProof,
                        },
                    });

                    if (retryResponse.ok) {
                        const result = await retryResponse.json();
                        alert(result.message || "Thank you for your donation!");
                        setPaymentInstructions(null);
                    } else {
                        const errorData = await retryResponse.json().catch(() => ({}));
                        throw new Error(
                            errorData.message || "Payment verification failed. Please try again."
                        );
                    }
                } catch (paymentError) {
                    setError(paymentError.message || "Payment failed. Please try again.");
                    return;
                }
            } else if (response.ok) {
                // Already paid or payment not required
                const result = await response.json();
                alert(result.message || "Thank you for your donation!");
            } else {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(errorData.message || `Request failed with status ${response.status}`);
            }
        } catch (err) {
            console.error("Donation error:", err);
            let errorMessage = "An error occurred. Please try again later.";
            if (err.message) {
                errorMessage = err.message;
            } else if (err.name === "TypeError" && err.message.includes("fetch")) {
                errorMessage = "Failed to connect to server. Please check your internet connection.";
            }
            setError(errorMessage);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div>
            <button
                style={{
                    ...buttonStyle,
                    opacity: isLoading ? 0.6 : 1,
                    cursor: isLoading ? "not-allowed" : "pointer",
                }}
                onClick={handleDonate}
                disabled={isLoading}
            >
                {isLoading ? "Processing..." : "Donate $5"}
            </button>
            {paymentInstructions && (
                <div
                    style={{
                        marginTop: "15px",
                        padding: "15px",
                        backgroundColor: "#f0f0f0",
                        borderRadius: "5px",
                        fontSize: "14px",
                    }}
                >
                    <p style={{ margin: "0 0 10px 0", fontWeight: "bold", color: "black" }}>
                        Payment Instructions:
                    </p>
                    <p style={{ margin: "5px 0", color: "black" }}>
                        Amount: {paymentInstructions.amount}
                    </p>
                    <p style={{ margin: "5px 0", color: "black" }}>
                        Network: {paymentInstructions.network}
                    </p>
                    <p style={{ margin: "5px 0", wordBreak: "break-all", color: "black" }}>
                        Recipient: {paymentInstructions.recipient}
                    </p>
                    <p style={{ margin: "10px 0 0 0", fontSize: "12px", color: "red" }}>
                        Please approve the transaction in your wallet...
                    </p>
                </div>
            )}
            {error && (
                <p
                    style={{
                        color: "#ff6b6b",
                        marginTop: "10px",
                        fontSize: "14px",
                        textAlign: "center",
                    }}
                >
                    {error}
                </p>
            )}
        </div>
    );
};

export default DonateButton;
